import { computed, defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";
import moment from "moment";

import {
    IPatientDetailsAxxessCare,
    IPayRate,
    IPostAxxessCareResponse,
    ISpecialtyAxessCare
} from "../../../resources-vue/vue-interfaces/i-visit-details";
import { PostToAxessCareDetails } from "../../../models/task.model";
import { TaskService } from "../../../services/task.service";
import { ToastrService } from "../../../services/toastr.service";
import { ApplicationEnum } from "../../../enums/ApplicationEnum";

import AddressModal from "../AddressModal/AddressModal.vue";
import PayrateModal from "../PayrateModal/PayrateModal.vue";

export default defineComponent({
    inject: ["dialogRef"],
    data() {
        return {
            _dialogService: null,
            _taskService: null,
            _toastrService: null,
            dialogController: null,
            dialogReference: this.dialogRef as any,
            isLoading: false as boolean,
            patientAddressDetailsAxxessCare: {} as IPatientDetailsAxxessCare,
            postToAxxessCareDetails: new PostToAxessCareDetails(),
            visitDetails: null,
            taskDetails: null,
            PayRates: [] as IPayRate[],
            dialogRef: null
        };
    },

    created() {
        this._taskService = Container.instance.get(TaskService);
        this._toastrService = Container.instance.get(ToastrService);
    },

    provide() {
        return {
            dialogRef: computed(() => this.dialogRef)
        };
    },

    async mounted() {
        this.visitDetails = Object.assign({}, this.dialogReference.data.visitDetails);
        this.isLoading = true;
        const specialties: Promise<ISpecialtyAxessCare[]> = this._taskService.getAxxessCareSpecialties({
            agencyId: this.visitDetails.agencyId,
            application: this.visitDetails.application
        });
        let details: Promise<IPatientDetailsAxxessCare>;
        let specialities: ISpecialtyAxessCare[];
        if (this.visitDetails.agencyId && this.visitDetails.PatientId) {
            details = this._taskService.getAxxessCareDetails(this.visitDetails.PatientId, {
                agencyId: this.visitDetails.agencyId,
                discipline: this.visitDetails.discipline,
                disciplineTask: this.visitDetails.DisciplineTaskId,
                application: this.visitDetails.application,
                episodeId: this.visitDetails.episodeId,
                taskId: this.visitDetails.eventId[0].taskId
            });
        }
        try {
            [specialities, this.patientAddressDetailsAxxessCare] = await Promise.all([specialties, details]);
            this.assignValues();
        } catch (error) {
            this._toastrService.error({
                title: `Error - Posting to AxessCare`,
                message: `There was a problem while posting this task to AxxessCare</b>. Please try again.`
            });
            this.dialogReference.cancel();
        } finally {
            this.isLoading = false;
        }
    },

    methods: {
        assignValues() {
            Object.assign(
                this.postToAxxessCareDetails,
                JSON.parse(JSON.stringify(this.patientAddressDetailsAxxessCare))
            );
            this.postToAxxessCareDetails.AgencyId = this.visitDetails.agencyId;
            this.postToAxxessCareDetails.EpisodeId = this.visitDetails.episodeId;
            this.postToAxxessCareDetails.PatientId = this.visitDetails.PatientId;
            this.postToAxxessCareDetails.EventId = this.visitDetails.eventId;
            this.postToAxxessCareDetails.TaskType = this.visitDetails.specialityName;
            this.postToAxxessCareDetails.ScheduleDate = this.visitDetails?.visitDates[0];
            this.postToAxxessCareDetails.SpecialtyId = this.visitDetails.specialityId;
            this.PayRates = this.postToAxxessCareDetails.PayRates;
        },

        async editAddress() {
            this.dialogRef = this.$dialog.open(AddressModal, {
                props: {
                    modal: true,
                    showHeader: false
                },

                data: {
                    address: this.patientAddressDetailsAxxessCare
                },

                onClose: (options) => {
                    if (options.data == "cancel") {
                        return;
                    } else {
                        if (options.data.Message == "success") {
                            Object.assign(this.patientAddressDetailsAxxessCare, options?.data);
                            this.postToAxxessCareDetails.AddressLine1 =
                                this.patientAddressDetailsAxxessCare.AddressLine1;
                            this.postToAxxessCareDetails.AddressLine2 =
                                this.patientAddressDetailsAxxessCare.AddressLine2;
                            this.postToAxxessCareDetails.StateCode = this.patientAddressDetailsAxxessCare.StateCode;
                            this.postToAxxessCareDetails.City = this.patientAddressDetailsAxxessCare.City;
                            this.postToAxxessCareDetails.ZipCode = this.patientAddressDetailsAxxessCare.ZipCode;
                        }
                    }
                }
            });
        },

        async editPayRate() {
            this.dialogRef = this.$dialog.open(PayrateModal, {
                props: {
                    modal: true,
                    showHeader: false
                },

                data: {
                    payRates: this.postToAxxessCareDetails?.PayRates
                },

                onClose: (options) => {
                    if (options.data == "cancel") {
                        return;
                    } else {
                        const isSuccess = options.data.every((item: IPayRate) => item.Message === "success");
                        if (isSuccess) {
                            this.PayRates = options.data;
                        }
                    }
                }
            });
        },

        cancel() {
            this.dialogReference.close();
        },

        async postToAxxessCare() {
            let payload = this.visitDetails.visitDates.map((date: any) => {
                this.postToAxxessCareDetails.AddressLine2 = this.patientAddressDetailsAxxessCare.AddressLine2;
                let eventId;
                if (this.visitDetails.application === ApplicationEnum.AgencyCore) {
                    eventId = this.postToAxxessCareDetails.EventId;
                } else {
                    const scheduleDate = moment(date).format("YYYY-MM-DD");
                    const marchingTask = this.visitDetails.eventId?.find((task: any) => {
                        const taskStartDate = moment(task.startDate).format("YYYY-MM-DD");
                        return taskStartDate === scheduleDate;
                    });
                    eventId = marchingTask?.taskId || this.visitDetails.eventId;
                }
                return {
                    UserId: this.visitDetails.userId,
                    Languages: "English",
                    CustomId: this.visitDetails.customId,
                    AgencyId: this.postToAxxessCareDetails.AgencyId,
                    EpisodeId: this.postToAxxessCareDetails.EpisodeId,
                    PatientId: this.postToAxxessCareDetails.PatientId,
                    EventId: eventId,
                    DisciplineTaskId: this.visitDetails.DisciplineTaskId,
                    VisitType: this.visitDetails.visitType,
                    ScheduleDate: `${moment(date).format("YYYY-MM-DD")}T00:00:00.000Z`,
                    TaskType: this.visitDetails.specialityId,
                    SpecialtyId: this.visitDetails.specialityId,
                    PrivateJob: this.postToAxxessCareDetails.PrivateJob,
                    AddressLine1: this.postToAxxessCareDetails.AddressLine1,
                    AddressLine2: this.postToAxxessCareDetails.AddressLine2,
                    StateCode: this.postToAxxessCareDetails.StateCode,
                    City: this.postToAxxessCareDetails.City,
                    ZipCode: this.postToAxxessCareDetails.ZipCode,
                    VisitDetails: this.postToAxxessCareDetails.VisitDetails,
                    HipaaInfo: this.postToAxxessCareDetails.HipaaInfo,
                    PayRates: this.postToAxxessCareDetails?.PayRates,
                    StartDate: `${moment(date).format("YYYY-MM-DD")}T00:00:00.000Z`,
                    EndDate: `${moment(date).format("YYYY-MM-DD")}T00:00:00.000Z`,
                    SpecialityName: this.visitDetails.specialityName,
                    PostalCode: this.postToAxxessCareDetails.PostalCode,
                    ActivePayInformationType: this.postToAxxessCareDetails.ActivePayInformationType,
                    isPostVisitInternally: this.postToAxxessCareDetails.PrivateJob,
                    Application: this.visitDetails.Application
                };
            });

            const result: IPostAxxessCareResponse = await this._taskService.multiplePostToAxxessCare({
                CareVisits: payload,
                CurrentAgencyId: this.postToAxxessCareDetails.AgencyId,
                CurrentUserId: this.visitDetails.userId,
                isPostVisitInternally: this.postToAxxessCareDetails.PrivateJob,
                Application: this.visitDetails.application
            });
            if (!result || !result.isSuccessful) {
                this._toastrService.error({
                    title: `Error - Posting to AxessCare`,
                    message: result.message
                });
                this.dialogReference.cancel();
            } else {
                this._toastrService.success({
                    title: `Posted to AxxessCare`,
                    message: `The task was posted successfully to AxxessCare`
                });
                this.dialogReference.close("success");
            }
        }
    }
});
